import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// ---- Transient-error retry interceptor -------------------------------------
// Rules (see D2):
//   * Only GET requests are ever retried. Any mutating verb bubbles up as-is.
//   * At most ONE automatic retry, ~800 ms after the first failure.
//   * Retry only on: no-response network errors, HTTP 5xx, or HTTP 404 (the
//     transient K8s-ingress-during-uvicorn-restart symptom). Real permanent
//     404s from a missing resource will also retry once — that's still safe
//     because the retry will observe the same 404 and give up.
//   * Guarded against loops via a per-request `_retriedOnce` marker.
// Reads only. This never masks a mutation failure from the caller.
const RETRY_STATUS = new Set([404, 500, 502, 503, 504]);
const RETRY_DELAY_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const config = error?.config;
        if (!config || config._retriedOnce) throw error;
        const method = (config.method || "get").toLowerCase();
        if (method !== "get") throw error;
        const status = error?.response?.status;
        // No response at all (DNS / connection reset / upstream unavailable),
        // or an eligible status code.
        const isNetworkError = !error?.response;
        if (!isNetworkError && !RETRY_STATUS.has(status)) throw error;
        config._retriedOnce = true;
        await sleep(RETRY_DELAY_MS);
        return api.request(config);
    },
);

export default api;

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
